/**
 * The hub shell: account strip, the briefing rail, the Town strip, and the one visible view.
 *
 * The rail is the spine of the hub — four verses sung in order (Calling, Loadout, Ground,
 * Deploy) — but it is only a *view* over `TabId`: every step is a tab that already
 * existed, `ctl.setTab` is still the only way anything moves, and Town keeps the rest of
 * Lanternfall one tap away. That is what lets `HubHandle` stay byte-identical.
 *
 * Views mount lazily and stay unmounted when hidden, so a tab that polls or holds a large
 * item list costs nothing while the player is staring at Deploy.
 */

import type { VNode } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { InboxMessage } from '../../net/api.js';
import { ROLE_BADGE, session } from '../../net/session.js';
import type { HubHost } from './index.js';
import {
  CALLINGS,
  DEFAULT_MAP_ID,
  ITEMS,
  ItemLocation,
  LEVEL_CAP,
  MAPS,
  type ItemStack,
  unlockReason,
  xpForLevel,
} from '@stillsun/shared';
import {
  type HubController,
  ListState,
  MORE_IDS,
  STEP_OF,
  TAB_ICONS,
  TAB_LABELS,
  type TabId,
  hubGate,
  levelProgress,
  loadList,
  onboardingProgress,
  takeNewlyUnlocked,
  useDeployDraft,
  useEscape,
  useHubView,
  useLoader,
  useProgress,
  useSession,
  when,
  wick,
} from './common.js';
import { Icon } from '../icons.js';
import { Auth } from './Auth.js';
import { CallingTab } from './Calling.js';
import { Changelog } from './Changelog.js';
import { Choir } from './Choir.js';
import { Comms } from './Comms.js';
import { Contracts } from './Contracts.js';
import { Deploy, Ground, groundSummary, visibleMaps } from './Deploy.js';
import { Marks } from './Marks.js';
import { Roadmap } from './Roadmap.js';
import { SettingsTab } from './Settings.js';
import { Skills } from './Skills.js';
import { Stash } from './Stash.js';
import { Terminal } from './Terminal.js';
import { Vendor } from './Vendor.js';
import { Workbench } from './Workbench.js';

export interface AppProps {
  host: HubHost;
  ctl: HubController;
}

/** Rail numerals, counted over the steps a player can currently see. */
const ROMAN = ['I', 'II', 'III', 'IV'] as const;

/** The primary weapon's name, which is the only loadout word that fits on a ribbon. */
function loadoutLine(items: ItemStack[] | null): string {
  if (items === null) return 'the ledger is silent';
  const primary = items.find((s) => s.location === ItemLocation.Primary);
  if (!primary) return 'empty hands';
  const name = ITEMS[primary.itemId]?.name ?? primary.itemId;
  const carried = items.filter(
    (s) =>
      s.location === ItemLocation.Primary ||
      s.location === ItemLocation.Secondary ||
      s.location === ItemLocation.Quick ||
      s.location === ItemLocation.Satchel,
  ).length;
  return `${name} · ${String(carried)} carried`;
}

export function App(props: AppProps): VNode {
  const { host, ctl } = props;
  const view = useHubView(ctl);
  const auth = useSession();
  const [authOpen, setAuthOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);

  const token = auth.token;
  const inbox = useLoader<InboxMessage[]>(
    () => loadList<InboxMessage>('/api/inbox', token, 'messages'),
    `inbox:${String(view.nonce)}:${token}`,
  );
  const unread = (inbox.data ?? []).filter((m) => m.read !== true).length;

  // The shell reads the stash once so both the rail (step II's line) and the deploy
  // summary ("carrying") can describe it. The Stash tab still owns its own copy, because
  // it mutates optimistically and must be free to roll the whole list back.
  const stash = useLoader<ItemStack[]>(
    () => loadList<ItemStack>('/api/stash', token, 'items'),
    `shell-stash:${String(view.nonce)}:${token}`,
  );

  const player = auth.player;
  const badge = ROLE_BADGE[auth.status === 'offline' ? 'none' : (player?.role ?? 'player')];
  const offline = auth.status === 'offline';

  // The XP bar is the one piece of M3 progression that belongs on every screen: it is the
  // answer to "why come back tomorrow", so it lives in the header rather than in a tab.
  const progress = useProgress(token, view.nonce);
  const level = progress.data?.level ?? player?.level ?? 1;
  const xp = progress.data?.xp ?? 0;
  const xpFrac = levelProgress(xp, level);
  const xpLabel =
    level >= LEVEL_CAP
      ? 'Level cap'
      : `${String(Math.max(0, xpForLevel(level + 1) - xp))} XP to ${String(level + 1)}`;
  const unspent = progress.data?.skillPoints ?? 0;

  // `null` = the ledger did not answer, `0` = answered and no Calling taken. Deploy needs
  // `progress.loaded` beside it: until the first fetch lands, neither reading is true yet.
  const callingId = progress.data?.calling ?? null;

  // Seed from the *visible* grounds, not from every ground the sim knows, and the first of
  // those is the city by construction (`visibleMaps` orders `DEFAULT_MAP_ID` first). A hub
  // that opened on the dev pad is what "yard is not the default anywhere" was written about.
  const deploy = useDeployDraft(visibleMaps()[0]?.id ?? DEFAULT_MAP_ID);

  // What Lanternfall opens for this ledger. Every input is server-owned progression data; the
  // gate only decides what is drawn, and every route behind a hidden tab is as authoritative as
  // it ever was. `progress.loaded` matters: until the first read lands we know nothing, and
  // guessing "locked" would flicker the whole town shut on every hub open.
  const gate = hubGate(onboardingProgress(progress, stash.data, player?.wick ?? 0));
  const open = gate.open;
  // The set is rebuilt every render, so nothing may depend on its identity: a `Set` in a
  // dependency array re-runs the effect on every single render. The membership string is the
  // stable thing, and it is what actually changed when a tab opens.
  const openKey = [...open].join(',');

  // A tab that is no longer reachable must not stay on screen — a level-1 player who was looking
  // at Ground when the ledger answered gets put back on Deploy rather than at a dead view.
  const locked = progress.loaded && !open.has(view.tab);
  useEffect(() => {
    if (locked) ctl.setTab('deploy');
  }, [ctl, locked]);

  const [moreOpen, setMoreOpen] = useState(false);
  // The Town strip scrolls horizontally, which means `overflow-y: hidden` — and an absolutely
  // positioned menu inside it was clipped to nothing (186 px of it, every size: all four
  // reading-room tabs were invisible and un-tappable). The menu is `position: fixed` now and
  // anchored to the chevron's own box the moment it opens, so it escapes the scroller.
  const moreBtn = useRef<HTMLButtonElement | null>(null);
  const [moreAt, setMoreAt] = useState({ top: 0, right: 0 });
  const [unlocked, setUnlocked] = useState<TabId[]>([]);
  // One toast per newly opened tab, once per browser. Deliberately keyed off the *set* of open
  // tabs rather than off a level-up event: the ladder can move for several reasons at once (a
  // raid pays Wick and XP in the same breath) and the player wants one honest list either way.
  //
  // Both reads have to have settled first. The stash is what answers `hasMaterials`, and it
  // lands *after* the progress document: run this on progress alone and a returning player gets
  // told the Workbench has just opened the instant their stash arrives, every single time they
  // walk into town. The toast is only honest once the whole ledger is in.
  const ledgerSettled = progress.loaded && !stash.loading;
  useEffect(() => {
    if (!ledgerSettled) return;
    const fresh = takeNewlyUnlocked(new Set(openKey.split(',') as TabId[]));
    if (fresh.length > 0) setUnlocked(fresh);
  }, [ledgerSettled, openKey]);

  const step = STEP_OF[view.tab];

  const stepLine = (n: number): string => {
    if (n === 1) {
      if (!progress.loaded) return 'reading…';
      if (callingId === null) return 'the ledger is silent';
      if (callingId === 0) return 'nobody yet — pick one';
      const named = CALLINGS[callingId]?.name ?? 'chosen';
      return unspent > 0
        ? `${named} · ${String(unspent)} point${unspent === 1 ? '' : 's'} unspent`
        : named;
    }
    if (n === 2) return loadoutLine(stash.data);
    if (n === 3) return groundSummary(deploy.draft);
    return 'the final check';
  };

  // A verse is "done" when it is actually answered, not merely when the player has walked
  // past it — a wine ribbon has to mean something or the rail is decoration.
  const stepDone = (n: number): boolean => {
    if (n === 1) return progress.loaded && callingId !== null && callingId !== 0;
    if (n === 2) return (stash.data ?? []).some((s) => s.location === ItemLocation.Primary);
    if (n === 3) return MAPS[deploy.draft.mapId] !== undefined;
    return false;
  };

  return (
    <div class="hub-shell">
      <div class="hub-drift" aria-hidden="true" />

      <header class="hub-head">
        <div class="hub-brand">
          {/* The lantern is the town's own mark; moon-on-plum, so it can ride as an <img>
              rather than being inlined for colour. */}
          <img
            class="hub-lantern"
            src="/brand/lanternfall-mark.svg"
            alt=""
            width="28"
            height="28"
          />
          <span class="hub-wordmark">Lanternfall</span>
          <span class="hub-sub">Between the raids</span>
        </div>

        <div class="hub-account">
          {offline ? (
            <span class="hub-offline">
              Account service offline — you can still deploy as a local guest.
            </span>
          ) : (
            <>
              <span class="hub-callsign">{player?.name ?? session.name}</span>
              {badge ? (
                <span class="hub-badge" style={{ color: badge.color, borderColor: badge.color }}>
                  {badge.label}
                </span>
              ) : null}
              <span class="hub-stat">
                <Icon name="wick" />
                <b>{wick(player?.wick ?? 0)}</b> Wick
              </span>
              <span class="hub-stat hub-xp" title={xpLabel}>
                <Icon name="level" />
                Level <b>{String(level)}</b>
                <span class="hub-xpbar">
                  <span class="hub-xpfill" style={{ width: `${String(xpFrac * 100)}%` }} />
                </span>
                <span class="hub-hint">{xpLabel}</span>
              </span>
              {unspent > 0 ? (
                <button
                  type="button"
                  class="hub-badge hub-points"
                  onClick={() => {
                    ctl.setTab('skills');
                  }}
                >
                  <Icon name="points" size={14} />
                  {`${String(unspent)} POINT${unspent === 1 ? '' : 'S'}`}
                </button>
              ) : null}
            </>
          )}
        </div>

        <div class="hub-headacts">
          <button
            type="button"
            class="hub-btn hub-btn-quiet"
            onClick={() => {
              setInboxOpen(true);
            }}
          >
            <Icon name="inbox" />
            Inbox
            {unread > 0 ? <span class="hub-pip">{String(unread)}</span> : null}
          </button>
          {auth.status === 'guest' || auth.status === 'anonymous' ? (
            <button
              type="button"
              class="hub-btn hub-btn-amber"
              onClick={() => {
                session.clearError();
                setAuthOpen(true);
              }}
            >
              <Icon name="secure" />
              Secure this account
            </button>
          ) : null}
          {auth.status === 'full' ? (
            <button
              type="button"
              class="hub-btn hub-btn-quiet"
              onClick={() => {
                session.signOut();
              }}
            >
              <Icon name="account" />
              Sign out
            </button>
          ) : null}
        </div>
      </header>

      <nav class="hub-town" aria-label="Lanternfall">
        <span class="hub-cap hub-town-cap">Town</span>
        {gate.town.map((id) => (
          <button
            key={id}
            type="button"
            class={`hub-tab${view.tab === id ? ' on' : ''}`}
            aria-current={view.tab === id ? 'page' : 'false'}
            onClick={() => {
              ctl.setTab(id);
            }}
          >
            <Icon name={TAB_ICONS[id]} size={20} />
            {TAB_LABELS[id]}
          </button>
        ))}

        {/* The reading room. Never locked, never in the strip: four words nobody needs on their
            first night, one chevron away for everybody else. */}
        <div class="hub-more">
          <button
            type="button"
            ref={moreBtn}
            class={`hub-tab hub-more-btn${MORE_IDS.includes(view.tab) ? ' on' : ''}`}
            aria-expanded={moreOpen ? 'true' : 'false'}
            onClick={() => {
              const box = moreBtn.current?.getBoundingClientRect();
              if (box) {
                setMoreAt({
                  top: Math.round(box.bottom + 1),
                  right: Math.round(window.innerWidth - box.right),
                });
              }
              setMoreOpen((v) => !v);
            }}
          >
            More
          </button>
          {moreOpen ? (
            <>
              <div
                class="hub-more-catch"
                onClick={() => {
                  setMoreOpen(false);
                }}
              />
              <div
                class="hub-more-menu"
                style={{ top: `${String(moreAt.top)}px`, right: `${String(moreAt.right)}px` }}
              >
                {MORE_IDS.map((id) => (
                  <button
                    key={id}
                    type="button"
                    class={`hub-tab${view.tab === id ? ' on' : ''}`}
                    aria-current={view.tab === id ? 'page' : 'false'}
                    onClick={() => {
                      setMoreOpen(false);
                      ctl.setTab(id);
                    }}
                  >
                    <Icon name={TAB_ICONS[id]} size={20} />
                    {TAB_LABELS[id]}
                  </button>
                ))}
              </div>
            </>
          ) : null}
        </div>
      </nav>

      <div class="hub-main">
        <nav class="hub-rail" aria-label="Briefing">
          <span class="hub-cap hub-rail-cap">The order of service</span>
          {gate.steps.map((s, i) => {
            const on = step === s.n;
            const done = !on && stepDone(s.n);
            // Numerals count the steps a player can see, not the authored four: a first-time
            // rail of Loadout → Deploy reads I · II, not II · IV.
            const numeral = ROMAN[i] ?? s.numeral;
            return (
              <button
                key={s.tab}
                type="button"
                class={`hub-rib s${String(s.n)}${done ? ' done' : ''}${on ? ' on' : ''}`}
                aria-current={on ? 'step' : 'false'}
                onClick={() => {
                  ctl.setTab(s.tab);
                }}
              >
                <span class="hub-rib-n">{numeral}</span>
                {/* Sibling of the numeral, never inside the body: the body's two lines are
                    nowrap + ellipsis and an icon in there eats the description at 390 px. */}
                <Icon name={s.icon} size={20} class="hub-rib-ic" />
                <span class="hub-rib-body">
                  <span class="hub-rib-t">{s.title}</span>
                  <span class="hub-rib-d">{stepLine(s.n)}</span>
                </span>
              </button>
            );
          })}
        </nav>

        <main class="hub-body">
          {view.tab === 'deploy' ? (
            <Deploy
              host={host}
              ctl={ctl}
              deploy={deploy}
              items={stash.data}
              calling={callingId}
              progressLoaded={progress.loaded}
              groundLocked={!open.has('ground')}
              onboarded={gate.onboarded}
            />
          ) : null}
          {view.tab === 'calling' ? (
            <CallingTab
              ctl={ctl}
              nonce={view.nonce}
              token={token}
              onChosen={() => {
                ctl.setTab('stash');
              }}
            />
          ) : null}
          {view.tab === 'skills' ? <Skills ctl={ctl} nonce={view.nonce} token={token} /> : null}
          {view.tab === 'stash' ? <Stash ctl={ctl} nonce={view.nonce} token={token} /> : null}
          {view.tab === 'ground' ? (
            <Ground
              deploy={deploy}
              nonce={view.nonce}
              token={token}
              onPicked={() => {
                ctl.setTab('deploy');
              }}
            />
          ) : null}
          {view.tab === 'workbench' ? <Workbench nonce={view.nonce} token={token} /> : null}
          {view.tab === 'vendor' ? <Vendor nonce={view.nonce} token={token} /> : null}
          {view.tab === 'contracts' ? <Contracts nonce={view.nonce} token={token} /> : null}
          {view.tab === 'marks' ? <Marks nonce={view.nonce} token={token} /> : null}
          {view.tab === 'terminal' ? <Terminal ctl={ctl} host={host} token={token} /> : null}
          {view.tab === 'roadmap' ? <Roadmap nonce={view.nonce} token={token} /> : null}
          {view.tab === 'changelog' ? <Changelog nonce={view.nonce} token={token} /> : null}
          {view.tab === 'choir' ? <Choir nonce={view.nonce} /> : null}
          {view.tab === 'settings' ? <SettingsTab host={host} /> : null}
        </main>
      </div>

      {/* The Lanternfall channel. Mounted only while the hub is up, which is also what
          stops it polling the moment the player deploys. */}
      {view.visible ? <Comms token={token} /> : null}

      {view.firstLight ? (
        <FirstLight
          ctl={ctl}
          name={player?.name ?? session.name}
          onClose={() => {
            ctl.clearFirstLight();
          }}
        />
      ) : null}

      {unlocked.length > 0 ? (
        <UnlockToast
          tabs={unlocked}
          onOpen={(id) => {
            setUnlocked([]);
            ctl.setTab(id);
          }}
          onClose={() => {
            setUnlocked([]);
          }}
        />
      ) : null}

      {authOpen ? (
        <Auth
          ctl={ctl}
          onClose={() => {
            setAuthOpen(false);
          }}
        />
      ) : null}

      {inboxOpen ? (
        <Inbox
          ctl={ctl}
          loading={inbox.loading}
          messages={inbox.data}
          onClose={() => {
            setInboxOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The one card that introduces the town, shown once, the moment a player walks out of The Waking.
 *
 * It exists because Lanternfall is the screen the owner called overwhelming, and the honest fix
 * is not only to show less of it but to say, out loud and once, that the rest is still there and
 * will arrive. Isolde says it, because she is the voice that has been talking for the last five
 * minutes and a stranger here would undo the whole tutorial.
 */
function FirstLight(props: { ctl: HubController; name: string; onClose: () => void }): VNode {
  useEscape(props.ctl, true, props.onClose);
  return (
    <div class="hub-scrim" onClick={props.onClose}>
      <div
        class="hub-modal hub-firstlight"
        role="dialog"
        aria-label="Lanternfall opens"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <span class="hub-cap">Isolde Vane</span>
        <h2 class="hub-firstlight-h">Lanternfall opens</h2>
        <p class="hub-firstlight-p">
          You came back, {props.name}. That is the whole job, and most do not manage it once.
        </p>
        <p class="hub-firstlight-p">
          This is the town. There is not much of it yet — a bed, a bench you have not earned, and
          one way out. It grows as you do. Everything that is shut has a reason, and the reason is
          always the same: you have not needed it yet.
        </p>
        <div class="hub-modal-acts">
          <button type="button" class="hub-sash" onClick={props.onClose}>
            Take the ground
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * "Lanternfall opens · Workbench".
 *
 * Not a modal — it must never stand between a returning player and the Deploy button. It is a
 * corner sash that says what opened and why, offers to walk you there, and goes away. Shown once
 * per browser per tab (see `takeNewlyUnlocked`), because the news stops being news.
 */
function UnlockToast(props: {
  tabs: TabId[];
  onOpen: (id: TabId) => void;
  onClose: () => void;
}): VNode {
  return (
    <div class="hub-unlock" role="status">
      <span class="hub-cap">Lanternfall opens</span>
      <ul class="hub-unlock-list">
        {props.tabs.map((id) => (
          <li key={id}>
            <button
              type="button"
              class="hub-unlock-row"
              onClick={() => {
                props.onOpen(id);
              }}
            >
              <Icon name={TAB_ICONS[id]} size={20} />
              <b>{TAB_LABELS[id]}</b>
              <span class="hub-hint">{unlockReason(id)}</span>
            </button>
          </li>
        ))}
      </ul>
      <button type="button" class="hub-btn hub-btn-quiet" onClick={props.onClose}>
        Dismiss
      </button>
    </div>
  );
}

interface InboxProps {
  ctl: HubController;
  loading: boolean;
  messages: InboxMessage[] | null;
  onClose: () => void;
}

function Inbox(props: InboxProps): VNode {
  useEscape(props.ctl, true, props.onClose);
  const list = props.messages ?? [];
  return (
    <div class="hub-scrim" onClick={props.onClose}>
      <div
        class="hub-modal"
        role="dialog"
        aria-label="Inbox"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <h2 class="hub-h2">Inbox</h2>
        <ListState
          loading={props.loading}
          offline={!props.loading && props.messages === null}
          empty={list.length === 0}
          emptyText="Nothing waiting. Replies to your feedback arrive here."
        />
        <div class="hub-list">
          {list.map((m) => {
            const date = when(m.ts);
            return (
              <article key={String(m.id)} class={`hub-msg${m.read === true ? '' : ' unread'}`}>
                <h3 class="hub-msg-title">{m.title ?? m.subject ?? 'Message'}</h3>
                {date.length > 0 ? <span class="hub-msg-date">{date}</span> : null}
                <p class="hub-msg-body">{m.body ?? m.text ?? ''}</p>
              </article>
            );
          })}
        </div>
        <div class="hub-modal-acts">
          <button type="button" class="hub-btn hub-btn-quiet" onClick={props.onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
